/**
 * StepUpDialog — reusable "sudo mode" re-authentication modal for sensitive
 * actions. Collects the current password and (when the account is MFA-enrolled,
 * or when the server demands it) a TOTP code, then runs the caller's action.
 *
 * SECURITY: the caller's `onConfirm` MUST submit through the untraced
 * `stepUpFetch` (client/src/lib/step-up-fetch.ts), NOT the traced `api.ts`
 * client — the credentials must never enter the telemetry apiTraces buffer.
 * This dialog only collects + branches on the standard step-up error codes.
 */

import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/auth/hooks/useAuth';
import { StepUpFetchError } from '@/lib/step-up-fetch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';

export interface StepUpCredentials {
  currentPassword: string;
  totpCode?: string;
}

export interface StepUpDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  /** Label for the confirm button (e.g. "Delete account"). */
  confirmLabel: string;
  /** Style the confirm button as destructive. */
  destructive?: boolean;
  /**
   * Runs the guarded action with the collected credentials. Should submit via
   * `stepUpFetch`. Throws `StepUpFetchError` on failure so this dialog can branch
   * (reveal the TOTP field on `MFA_CODE_REQUIRED`, show code/password errors).
   */
  onConfirm: (creds: StepUpCredentials) => Promise<void>;
  /** Called after a successful confirm (the dialog has already closed). */
  onSuccess?: () => void;
}

function errorCode(err: unknown): string | undefined {
  return err instanceof StepUpFetchError ? err.code : undefined;
}

export function StepUpDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  destructive,
  onConfirm,
  onSuccess,
}: StepUpDialogProps) {
  const { user } = useAuth();
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Prompt for a code up front when the token hint says enrolled; the server can
  // still force it later (stale hint) via MFA_CODE_REQUIRED.
  useEffect(() => {
    if (open) {
      setPassword('');
      setTotp('');
      setNeedsTotp(user?.mfaEnrolled ?? false);
      setError('');
      setSubmitting(false);
    }
  }, [open, user?.mfaEnrolled]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || (needsTotp && totp.length !== 6)) return;
    setSubmitting(true);
    setError('');
    try {
      await onConfirm({ currentPassword: password, totpCode: needsTotp ? totp : undefined });
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      const code = errorCode(err);
      if (code === 'MFA_CODE_REQUIRED') {
        setNeedsTotp(true);
        setError('Enter the 6-digit code from your authenticator app.');
      } else if (code === 'MFA_CODE_MISMATCH') {
        setError('That authentication code was incorrect. Try a fresh code.');
      } else if (code === 'INVALID_PASSWORD' || code === 'INCORRECT_CURRENT_PASSWORD') {
        setError('Your current password is incorrect.');
      } else if (code === 'MFA_STEP_UP_EXPIRED') {
        setError('That took too long — please try again.');
      } else {
        setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent>
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description ?? 'Confirm it’s you to continue.'}</DialogDescription>
          </DialogHeader>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="space-y-2">
            <Label htmlFor="step-up-password">Current password</Label>
            <PasswordInput
              id="step-up-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              autoFocus
              disabled={submitting}
              required
            />
          </div>

          {needsTotp && (
            <div className="space-y-2">
              <Label htmlFor="step-up-totp">Authentication code</Label>
              <Input
                id="step-up-totp"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
                value={totp}
                onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                disabled={submitting}
                className="text-center font-mono tracking-[0.4em]"
              />
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant={destructive ? 'destructive' : 'default'}
              disabled={submitting || !password || (needsTotp && totp.length !== 6)}
            >
              {submitting && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              {confirmLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
